import type { CSSProperties, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { gsap } from '@/lib/gsap'

interface RevealProps {
    children: ReactNode
    className?: string
    id?: string
    /** 'load' animates on mount (staggered hero), 'scroll' animates when scrolled into view. */
    trigger?: 'load' | 'scroll'
    delay?: number
    duration?: number
    y?: number
    x?: number
    scale?: number
    ease?: string
    start?: string
    style?: CSSProperties
}

function initialTransform(y: number, x: number, scale: number): string {
    return `translate3d(${x}px, ${y}px, 0) scale(${scale})`
}

export function Reveal({
    children,
    className,
    id,
    trigger = 'scroll',
    delay = 0,
    duration = 0.8,
    y = 20,
    x = 0,
    scale = 1,
    ease = 'power2.out',
    start = 'top 90%',
    style
}: RevealProps) {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = ref.current

        if (!el) return

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduce) {
            gsap.set(el, { clearProps: 'opacity,transform' })

            return
        }

        const ctx = gsap.context(() => {
            const from = { opacity: 0, y, x, scale }
            const to = {
                opacity: 1,
                y: 0,
                x: 0,
                scale: 1,
                duration,
                ease,
                clearProps: 'transform'
            }

            if (trigger === 'load') {
                gsap.fromTo(el, from, { ...to, delay })
            } else {
                gsap.fromTo(el, from, {
                    ...to,
                    scrollTrigger: { trigger: el, start, once: true }
                })
            }
        }, el)

        return () => ctx.revert()
    }, [trigger, delay, duration, y, x, scale, ease, start])

    return (
        <div
            ref={ref}
            id={id}
            className={className}
            style={{ opacity: 0, transform: initialTransform(y, x, scale), ...style }}
        >
            {children}
        </div>
    )
}
